import { resolvePredefinedAssetCategoryName } from './asset-category-icon.model';
import { AssetCategory, PredefinedAssetCategoryKeys } from './asset-category.model';

/** Base path to the SVG files used by the predefined category icons and colored previews. */
export const ASSET_CATEGORY_ICON_ASSETS_URL = 'assets/simon-theme/icons/asset-categories/';

/** Base path to the SVG files used by the category icon picker. */
export const ASSET_CATEGORY_PICKER_ICON_ASSETS_URL = 'assets/simon-theme/icons/category_icons/';

/** `sim-icon` name prefix distinguishing picker icons from the predefined category icons. */
const PICKER_ICON_PREFIX = 'category_';

/** Resolves the SVG asset url for a pickable asset category icon. */
export function assetCategoryIconUrl(icon: string): string {
  if (icon.startsWith(PICKER_ICON_PREFIX)) {
    return `${ASSET_CATEGORY_PICKER_ICON_ASSETS_URL}${icon.slice(PICKER_ICON_PREFIX.length)}.svg`;
  }
  return `${ASSET_CATEGORY_ICON_ASSETS_URL}${icon}.svg`;
}

/** Picker icon display order; the explicit number is the icon's fixed position in the picker grid. */
const ASSET_CATEGORY_PICKER_ICON_ORDER = {
  1: 'other',
  2: 'multimedia',
  3: 'home_appliances',
  4: 'office_equipment',
  5: 'furniture',
  6: 'decoration',
  7: 'lighting_systems',
  8: 'electrical',
  9: 'plumbing',
  10: 'construction',
  11: 'finishing_materials',
  12: 'tools',
  13: 'hammer',
  14: 'plants',
  15: 'garden',
  16: 'cctv',
  17: 'wifi',
  18: 'fingerprints',
  19: 'security',
  20: 'fire_safety',
  21: 'sanitary_and_hygiene',
  22: 'medical',
  23: 'wallet_key',
  24: 'unlock',
  25: 'cube_arrow',
  26: 'wind_turbine',
  27: 'machines',
  28: 'spare_parts',
  29: 'res',
  30: 'hvac_systems_a',
  31: 'hvac_systems_b',
  32: 'hvac_systems_c',
  33: 'gauge',
  34: 'thermometer',
  35: 'network',
  36: 'electronics',
  37: 'circuit',
  38: 'transport',
  39: 'transport_mix',
  40: 'transport_tram',
  41: 'transport_bus',
  42: 'transport_truck',
  43: 'transport_plane',
  44: 'first_aid',
  45: 'parking',
  46: 'accessible',
  47: 'exit',
  48: 'elevator',
  49: 'escalator_up',
  50: 'escalator_down',
  51: 'stairs_up',
  52: 'stairs_down',
  53: 'book',
  54: 'hobby',
  55: 'film_reel',
  56: 'music_note',
  57: 'gaming_pad',
  58: 'camera',
  59: 'images',
  60: 'pets',
  61: 'kids',
  62: 'education',
  63: 'briefcase',
  64: 'contact_page',
  65: 'documents',
  66: 'invoice_cube',
  67: 'analytics_doc',
  68: 'analytics_chart',
  69: 'flask_a',
  70: 'flask_b',
  71: 'test_tubes',
  72: 'microscope',
  73: 'virus',
  74: 'atom',
  75: 'orbit',
  76: 'chemistry_formula',
  77: 'cutlery',
  78: 'mug',
  79: 'bowl',
  80: 'hot_soup',
  81: 'glass_wine',
  82: 'organic_food',
  83: 'healthy_food',
  84: 'fastfood'
} as const satisfies Record<number, string>;

/** `sim-icon` identifiers of the icons pickable for a custom asset category. */
export type AssetCategoryPickerIconName =
  `${typeof PICKER_ICON_PREFIX}${(typeof ASSET_CATEGORY_PICKER_ICON_ORDER)[keyof typeof ASSET_CATEGORY_PICKER_ICON_ORDER]}`;

/** Icons available in the category icon picker, in display order. */
export const ASSET_CATEGORY_PICKER_ICONS: AssetCategoryPickerIconName[] = Object.entries(
  ASSET_CATEGORY_PICKER_ICON_ORDER
)
  .sort(([a], [b]) => Number(a) - Number(b))
  .map(([, name]) => `${PICKER_ICON_PREFIX}${name}` as AssetCategoryPickerIconName);

/** Default icon preselected in the picker for a new custom category. */
export const ASSET_CATEGORY_DEFAULT_PICKER_ICON: AssetCategoryPickerIconName = ASSET_CATEGORY_PICKER_ICONS[0];

type PickerIconBaseName = (typeof ASSET_CATEGORY_PICKER_ICON_ORDER)[keyof typeof ASSET_CATEGORY_PICKER_ICON_ORDER];

/** Picker order position by icon name (without the `category_` prefix). */
const PICKER_ICON_POSITION = new Map<string, number>(
  Object.entries(ASSET_CATEGORY_PICKER_ICON_ORDER).map(([position, name]) => [name, Number(position)])
);

/** Picker order icon representing each predefined category (their icon names differ from the picker ones). */
const PREDEFINED_CATEGORY_PICKER_NAME: Record<PredefinedAssetCategoryKeys, PickerIconBaseName> = {
  None: 'other',
  Multimedia: 'multimedia',
  HomeAppliances: 'home_appliances',
  OfficeEquipment: 'office_equipment',
  Furniture: 'furniture',
  Decoration: 'decoration',
  LightingSystems: 'lighting_systems',
  Plants: 'plants',
  Documents: 'documents',
  Electrical: 'electrical',
  Security: 'security',
  FireSafety: 'fire_safety',
  Plumbing: 'plumbing',
  SanitaryAndHygiene: 'sanitary_and_hygiene',
  HVACSystems: 'hvac_systems_a',
  RES: 'res',
  Hobby: 'hobby',
  Transport: 'transport',
  Education: 'education',
  Tools: 'tools',
  Garden: 'garden',
  Machines: 'machines',
  SpareParts: 'spare_parts',
  Pets: 'pets',
  Kids: 'kids',
  FinishingMaterials: 'finishing_materials',
  Construction: 'construction',
  Other: 'other'
};

/** Categories without a matching picker icon (e.g. custom image ones) sort after every ordered icon. */
const UNORDERED_POSITION = Number.MAX_SAFE_INTEGER;

function assetCategoryPickerOrderPosition(category: AssetCategory): number {
  const icon = category.icon;
  if (icon) {
    const name = icon.startsWith(PICKER_ICON_PREFIX) ? icon.slice(PICKER_ICON_PREFIX.length) : icon;
    const position = PICKER_ICON_POSITION.get(name);
    if (position !== undefined) return position;
  }

  if (category.isPredefined) {
    const name = resolvePredefinedAssetCategoryName(category.predefinedCategory);
    if (name) return PICKER_ICON_POSITION.get(PREDEFINED_CATEGORY_PICKER_NAME[name]) ?? UNORDERED_POSITION;
  }

  return UNORDERED_POSITION;
}

/**
 * Sorts asset categories by their icon's position in the picker order; categories without
 * a matching picker icon keep their relative order at the end of the list.
 */
export function compareAssetCategoriesByPickerOrder(a: AssetCategory, b: AssetCategory): number {
  return assetCategoryPickerOrderPosition(a) - assetCategoryPickerOrderPosition(b);
}

/** Default colors applied when switching to the custom color icon style. */
export const ASSET_CATEGORY_DEFAULT_ICON_COLOR = '#000000';
export const ASSET_CATEGORY_DEFAULT_BACKGROUND_COLOR = '#FFFFFF';

/** General color palette for the custom color icon style (10x8 grid, same as the room color palette). */
export const ASSET_CATEGORY_COLORS: string[] = [
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#B7B7B7',
  '#CCCCCC',
  '#D9D9D9',
  '#EFEFEF',
  '#F3F3F3',
  '#FFFFFF',
  '#8F1109',
  '#F02517',
  '#F59B18',
  '#FEFD1C',
  '#63FB0A',
  '#65FDFE',
  '#5688E7',
  '#1122FE',
  '#9129FE',
  '#F034FF',
  '#E1B9B0',
  '#EFCDCC',
  '#F9E5CE',
  '#FDF2CD',
  '#DBE9D3',
  '#D2E0E3',
  '#CCDAF8',
  '#D2E2F3',
  '#D8D3E9',
  '#E7D2DC',
  '#C1472A',
  '#E19B9A',
  '#F3CB9D',
  '#FCE59B',
  '#BBD6A8',
  '#A7C4C9',
  '#A9C2F3',
  '#A5C5E7',
  '#B3A8D6',
  '#D0A7BD',
  '#C1472A',
  '#D56A68',
  '#EEB36D',
  '#FAD969',
  '#9AC27D',
  '#7EA4AF',
  '#769FEA',
  '#79A8DB',
  '#8C7EC2',
  '#BB7DA0',
  '#9C250B',
  '#C01B10',
  '#DD923D',
  '#EBC239',
  '#74A650',
  '#50808E',
  '#497AD7',
  '#4C85C5',
  '#6551A6',
  '#9E5179',
  '#7D2511',
  '#8F1109',
  '#AC6112',
  '#B99010',
  '#44741E',
  '#224F5C',
  '#2658CB',
  '#215493',
  '#332074',
  '#6D2047',
  '#551303',
  '#5F0804',
  '#72400A',
  '#7B6007',
  '#2E4D14',
  '#16343D',
  '#254686',
  '#153863',
  '#1F154D',
  '#471430'
];
